/**
 * ErrorPanel — used by every page-level surface that owns a useQuery to
 * surface API failures instead of rendering a misleading "no data" state.
 *
 * Bundles the EmptyState + AlertCircle icon + retry button so each page
 * only needs one line in its render path:
 *
 *   if (query.isError || !query.data) return <ErrorPanel onRetry={query.refetch} />;
 *
 * The default copy is the generic "Couldn't load — try again" pair. Pages
 * with domain context can override (errorTitle for the page name, etc).
 */

import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { EmptyState } from './empty-state';

export interface ErrorPanelProps {
  title?: string;
  body?: ReactNode;
  onRetry?: () => void | Promise<unknown>;
  /** Hide the retry button if the caller wants to use their own affordance. */
  hideRetry?: boolean;
}

export function ErrorPanel({
  title = "Couldn't load",
  body = 'The server didn’t return a response. This is usually a transient issue.',
  onRetry,
  hideRetry = false,
}: ErrorPanelProps) {
  return (
    <EmptyState
      icon={<AlertCircle className="h-6 w-6" />}
      title={title}
      body={body}
      action={
        !hideRetry && onRetry ? (
          <button
            type="button"
            onClick={() => void onRetry()}
            className="rounded-sm border border-border-default px-3 py-1 text-xs text-secondary hover:border-border-strong hover:text-primary"
          >
            Try again
          </button>
        ) : null
      }
    />
  );
}
